import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { getAssistantWalkthroughFeatures } from "api/services/chat/assistant";

import { ASSISTANT_FEATURE_QUERY_PARAM } from "../../../constants";
import { AssistantScenarioStatusEnumType } from "../../../enums";
import {
  IAssistantFeatureWalkthroughState,
  IUseAssistantFeatureSessionOptions,
  IUseAssistantFeatureSessionReturn,
} from "../types";

export const useAssistantFeatureSession = ({
  identity,
  page,
  saving,
}: IUseAssistantFeatureSessionOptions): IUseAssistantFeatureSessionReturn => {
  const location = useLocation();
  const [requestedId, setRequestedId] = useState<string | null>(null);
  const [state, setState] = useState<IAssistantFeatureWalkthroughState>({
    identity,
  });
  const generation = useRef(0);
  const request = useRef(0);

  useEffect(() => {
    setRequestedId(
      new URLSearchParams(location.search).get(ASSISTANT_FEATURE_QUERY_PARAM)
    );
  }, [location.search]);

  const handleLoad = useCallback(async () => {
    if (!requestedId || !page) return;
    const context = generation.current;
    const current = ++request.current;

    try {
      const features = await getAssistantWalkthroughFeatures({
        walkthroughFeatureId: requestedId,
      });
      if (context === generation.current && current === request.current) {
        setState({
          identity,
          feature: features.find((feature) => feature.guide.page === page),
        });
      }
    } catch {
      // Apollo owns ordinary request errors.
    }
  }, [identity, page, requestedId]);

  useEffect(() => {
    generation.current += 1;
    request.current += 1;
    setState({ identity });
    void handleLoad();

    const refresh = () => {
      if (document.visibilityState === "visible" && !saving.current)
        void handleLoad();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);

    return () => {
      generation.current += 1;
      request.current += 1;
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [identity, handleLoad, saving]);

  const feature =
    state.identity === identity && state.feature?.guide.page === page
      ? state.feature
      : undefined;

  const handleProgressSaved = useCallback(
    async (guideId: string, status: AssistantScenarioStatusEnumType) => {
      if (!feature) return;

      setState((current) =>
        current.identity === identity && current.feature?.id === feature.id
          ? {
              ...current,
              feature: {
                ...current.feature,
                scenarios: current.feature.scenarios.map((scenario) =>
                  scenario.guide.id === guideId
                    ? { ...scenario, status }
                    : scenario
                ),
              },
            }
          : current
      );
      await handleLoad();
    },
    [feature, identity, handleLoad]
  );

  const handleClose = useCallback(() => {
    generation.current += 1;
    request.current += 1;
    setRequestedId(null);
    setState({ identity });
  }, [identity]);

  return {
    active: Boolean(requestedId),
    feature,
    generation,
    request,
    handleLoad,
    handleProgressSaved,
    handleClose,
  };
};
